import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";

import { useResourcesList } from "./resourcesList";
import { updateSearchTerms } from "../../reducers/resourcesList/search";

// Sync the resources list search terms with a URL query param.
//
// This is meant to support deep links that pre-fill the search input (e.g. clicking a
// word in a word cloud).
//
// It only applies changes when the URL param changes, so it does not prevent the user
// from editing/clearing the search afterwards.
const ResourcesListUrlSearchSync = ({ paramName = "search" }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { resourcesListKey, search } = useResourcesList();
  const currentTerms = search?.terms || "";

  // Track the last URL param we applied to avoid re-applying it when the user edits the
  // search input (which changes Redux state but not the URL).
  const lastAppliedParamRef = useRef(null);

  useEffect(() => {
    if (!resourcesListKey) return;

    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get(paramName);

    const previousAppliedParam = lastAppliedParamRef.current;
    if (searchParam === previousAppliedParam) return;

    lastAppliedParamRef.current = searchParam;

    // If the param was removed, clear the terms only if we're still on the previously
    // applied value (avoid wiping a user-entered term).
    if (searchParam === null) {
      if (previousAppliedParam && currentTerms === previousAppliedParam) {
        dispatch(updateSearchTerms(resourcesListKey, ""));
      }

      return;
    }

    if (searchParam !== currentTerms) {
      dispatch(updateSearchTerms(resourcesListKey, searchParam));
    }
  }, [currentTerms, dispatch, location.search, paramName, resourcesListKey]);

  return null;
};

export default ResourcesListUrlSearchSync;
